import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../UI/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiEye, FiEyeOff } = FiIcons;

const UserForm = ({ user, onSubmit, onCancel, submitting }) => {
  const { departments, roles } = useCRM();
  const { isAdmin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      full_name: '',
      email: '',
      roleId: '',
      department_id: '',
      phone: '',
      date_of_joining: '',
      date_of_birth: '',
      is_active: true,
      newPassword: '',
      confirmPassword: ''
    }
  });

  const newPassword = watch('newPassword');

  // Reset form when user prop changes
  useEffect(() => {
    if (user) {
      reset({
        full_name: user.full_name,
        email: user.email,
        roleId: user.role_id,
        department_id: user.department_id,
        phone: user.phone,
        date_of_joining: user.date_of_joining,
        date_of_birth: user.date_of_birth,
        is_active: user.is_active,
        newPassword: '',
        confirmPassword: ''
      });
      setChangePasswordMode(false);
    } else {
      reset({
        full_name: '',
        email: '',
        roleId: '',
        department_id: '',
        phone: '',
        date_of_joining: '',
        date_of_birth: '',
        is_active: true,
        newPassword: '',
        confirmPassword: ''
      });
      setChangePasswordMode(false);
    }
  }, [user, reset]);

  const handleFormSubmit = (data) => {
    const selectedRole = roles.find(r => r.id === data.roleId);
    
    // Clean up data before submitting
    const formattedData = {
      ...data,
      role: selectedRole?.name || 'Employee',
      is_active: Boolean(data.is_active)
    };
    
    // If not changing password or field is empty, remove it to avoid accidental overwrite
    if (!changePasswordMode || !data.newPassword) {
      delete formattedData.newPassword;
      delete formattedData.confirmPassword;
    }

    onSubmit(formattedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            {...register('full_name', { 
              required: 'Full name is required',
              minLength: { value: 2, message: 'Full name must be at least 2 characters' }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter full name"
            disabled={submitting}
          />
          {errors.full_name && (
            <p className="mt-1 text-sm text-red-600">{errors.full_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter email address"
            disabled={submitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role *
          </label>
          <select
            {...register('roleId', { required: 'Role is required' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            disabled={submitting}
          >
            <option value="">Select role</option>
            {roles?.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {errors.roleId && (
            <p className="mt-1 text-sm text-red-600">{errors.roleId.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Department
          </label>
          <select
            {...register('department_id')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            disabled={submitting}
          >
            <option value="">Select department</option>
            {departments?.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            {...register('phone')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter phone number"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Joining
          </label>
          <input
            type="date"
            {...register('date_of_joining')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Birth
          </label>
          <input
            type="date"
            {...register('date_of_birth')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            disabled={submitting}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            {...register('is_active')}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            disabled={submitting}
          />
          <label className="ml-2 block text-sm text-gray-900">
            Active User
          </label>
        </div>
      </div>

      {/* Admin Password Reset Section - Only visible in Edit mode for Admins */}
      {user && isAdmin && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center">
              <SafeIcon icon={FiLock} className="h-4 w-4 mr-2" />
              Change Password
            </h3>
            <button
              type="button"
              onClick={() => setChangePasswordMode(!changePasswordMode)}
              className="text-xs text-primary-600 hover:text-primary-800 font-medium"
            >
              {changePasswordMode ? 'Cancel Change' : 'Reset Password'}
            </button>
          </div>

          {changePasswordMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('newPassword', { 
                      required: changePasswordMode ? 'New password is required' : false,
                      minLength: { value: 6, message: 'Password must be at least 6 characters' }
                    })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter new password"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-4 w-4" />
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirmPassword', { 
                      required: changePasswordMode ? 'Please confirm password' : false,
                      validate: val => !changePasswordMode || val === newPassword || 'Passwords do not match'
                    })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Confirm new password"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="h-4 w-4" />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Default Passwords Hint - Only for New Users */}
      {!user && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Default Passwords</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Admin: <code className="bg-blue-100 px-1 rounded">admin123</code></li>
            <li>• Manager: <code className="bg-blue-100 px-1 rounded">manager123</code></li>
            <li>• Employee: <code className="bg-blue-100 px-1 rounded">employee123</code></li>
          </ul>
          <p className="text-xs text-blue-600 mt-2">
            Users can change their passwords after first login
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={submitting}
          loading={submitting}
        >
          {user ? 'Update User' : 'Create User'}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;